import { Fragment, useState } from "react";
import { cn } from "@/lib/cn";
import { CartaoDeCategoria } from "@/components/comecar/selecao/CartaoDeCategoria";
import { CartaoDeItem } from "@/components/comecar/selecao/CartaoDeItem";
import { AdicionarNaCategoria } from "@/components/comecar/selecao/AdicionarNaCategoria";
import type { OfertaDeMaterial } from "./MateriaisECoresDoServico";

export const chipCls = (ativo: boolean) =>
  cn(
    "rounded-md border px-2.5 py-1 text-left text-xs font-medium transition-colors",
    ativo
      ? "border-brand-300 bg-brand-50 text-brand-700 dark:border-primary/40 dark:bg-primary/10 dark:text-primary"
      : "border-zinc-200 bg-white text-zinc-600 hover:border-brand-300 dark:border-border dark:bg-card dark:text-muted-foreground",
  );

type Props = {
  ofertas: OfertaDeMaterial[];
  /** Os que a base não sugere pra este serviço (o onboarding mostra primeiro os compatíveis). */
  outras: OfertaDeMaterial[];
  materialIds: string[];
  onAlternar: (id: string) => void;
  /** Buscando: todas as famílias abertas. */
  abertos?: boolean;
  /** Configurando vários serviços: os que só parte deles tem. */
  parciais?: string[];
  /** "Adicionar" dentro de cada família aberta: o material novo nasce nela, marcado. */
  onNovoNaFamilia?: (familia: string, nome: string) => void;
};

/**
 * Materiais por família no mesmo desenho da escolha de serviços (pedido do Jun): um cartão por
 * família com "marcados/total"; abrir mostra os materiais em cartões de marcar logo abaixo da linha.
 * Famílias com material marcado ou sugerido vêm primeiro; dentro, os sugeridos primeiro.
 */
export function BlocosDeMateriais({ ofertas, outras, materialIds, onAlternar, abertos, parciais = [], onNovoNaFamilia }: Props) {
  const [aberta, setAberta] = useState<string | null>(null);
  const todos = [...ofertas, ...outras];
  const grupoDe = (m: OfertaDeMaterial) => m.grupo ?? "Outros";
  const familias = [...new Set(todos.map(grupoDe))];
  const peso = (g: string) =>
    todos.some((m) => grupoDe(m) === g && (materialIds.includes(m.id) || parciais.includes(m.id))) ? 0 : ofertas.some((m) => grupoDe(m) === g) ? 1 : 2;
  const ordenadas = [...familias].sort((a, b) => peso(a) - peso(b));
  const daFamilia = (g: string) => [...ofertas.filter((m) => grupoDe(m) === g), ...outras.filter((m) => grupoDe(m) === g)];

  const itens = (g: string, comAdicionar = false) => (
    <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
      {daFamilia(g).map((m) => (
        <div key={m.id} className={m.active === false ? "opacity-50" : undefined} title={m.active === false ? `${m.label} (desativado)` : undefined}>
          <CartaoDeItem
            titulo={m.label}
            estado={materialIds.includes(m.id) ? "marcado" : parciais.includes(m.id) ? "parcial" : "normal"}
            detalhe={!materialIds.includes(m.id) && parciais.includes(m.id) ? "Em alguns" : undefined}
            onClick={() => onAlternar(m.id)}
          />
        </div>
      ))}
      {comAdicionar && onNovoNaFamilia && <AdicionarNaCategoria categoria={g} onAdicionar={(nome) => onNovoNaFamilia(g, nome)} />}
    </div>
  );

  if (abertos) {
    return (
      <div className="space-y-3">
        {ordenadas.map((g) => (
          <section key={g}>
            <p className="mb-1.5 text-[11.5px] font-semibold text-zinc-800 dark:text-foreground">{g}</p>
            {itens(g)}
          </section>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-flow-row-dense grid-cols-1 gap-2 sm:grid-cols-2">
      {ordenadas.map((g) => {
        const lista = daFamilia(g);
        const marcados = lista.filter((m) => materialIds.includes(m.id)).length;
        const emAlguns = lista.filter((m) => !materialIds.includes(m.id) && parciais.includes(m.id)).length;
        const estaAberta = aberta === g;
        return (
          <Fragment key={g}>
            <CartaoDeCategoria
              titulo={g}
              detalhe={`${lista.length} ${lista.length === 1 ? "material" : "materiais"}${emAlguns ? ` · ${emAlguns} em alguns` : ""}`}
              marcados={marcados}
              total={lista.length}
              aberto={estaAberta}
              compacto
              onClick={() => setAberta(estaAberta ? null : g)}
            />
            {estaAberta && (
              <div className="col-span-full rounded-lg border border-brand-200 bg-white p-2.5 shadow-sm dark:border-primary/30 dark:bg-card">{itens(g, true)}</div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}
